import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged, 
  User, 
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

type UserRole = 'admin' | 'user';

interface AuthContextType {
  user: User | null;
  role: UserRole;
  isActive: boolean;
  loading: boolean;
  logout: () => Promise<void>;
  setRememberMe: (enabled: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>('user');
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userRef = doc(db, 'users', firebaseUser.uid);
        const userSnap = await getDoc(userRef);
        
        let currentRole: UserRole = 'user';
        let currentStatus: boolean = false;
        
        // Special Bootstrap: Force mbui41@gmail.com to be Admin and Active
        const isSuperAdmin = firebaseUser.email === 'mbui41@gmail.com';

        if (!userSnap.exists()) {
          const newUser = {
            email: firebaseUser.email,
            createdAt: Date.now(),
            lastLogin: Date.now(),
            role: isSuperAdmin ? 'admin' : 'user',
            isActive: isSuperAdmin ? true : false // New users are inactive by default
          };
          await setDoc(userRef, newUser);
          currentRole = newUser.role as UserRole;
          currentStatus = newUser.isActive;
        } else {
          const data = userSnap.data();
          currentRole = data.role as UserRole;
          currentStatus = data.isActive ?? false;
          
          // If they are the super admin email but don't have the role or active status, update it
          if (isSuperAdmin && (currentRole !== 'admin' || !currentStatus)) {
            await updateDoc(userRef, { role: 'admin', isActive: true });
            currentRole = 'admin';
            currentStatus = true;
          }
          
          await setDoc(userRef, { lastLogin: Date.now() }, { merge: true });
        }
        
        setRole(currentRole);
        setIsActive(currentStatus);
      } else {
        setRole('user');
        setIsActive(false);
      }
      setUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  const setRememberMe = async (enabled: boolean) => {
    await setPersistence(auth, enabled ? browserLocalPersistence : browserSessionPersistence);
  };

  return (
    <AuthContext.Provider value={{ user, role, isActive, loading, logout, setRememberMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};